import React, { useState } from 'react'
import { Feature } from '../api/client'
import { renderCollectionItem } from '../utils/collectionUtils'
import CollectionItemModal from './collections/CollectionItemModal'
import PersonAvatar from './PersonAvatar'
import { PersonDetailValue } from './checkin/PersonDetailModal'

interface ReferenceListItemProps {
    collectionId: number
    itemId: number
    collectionItems: Record<string, any>
    onRefresh?: () => void
    /** When the row points at a People collection item and this is provided,
     *  taps route through here instead of opening the generic
     *  `CollectionItemModal`. Matches the behavior of `PersonChips`. */
    onPersonClick?: (value: PersonDetailValue) => void
}

const ReferenceListItem: React.FC<ReferenceListItemProps> = ({ collectionId, itemId, collectionItems, onRefresh, onPersonClick }) => {
    const [showModal, setShowModal] = useState(false)
    const item = collectionItems[String(itemId)]
    const collection = item?.collection

    if (!item || !collection) {
        return <div className="text-gray-500 text-sm">#{itemId}</div>
    }

    const isPeople = collection.system_kind === 'people'
    const pictureField = Object.keys(collection.config?.fields || {}).find(
        key => collection.config.fields[key].type === 'picture'
    )
    const pictureValue = pictureField ? item.data?.[pictureField] : null

    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        if (isPeople && onPersonClick) {
            onPersonClick({ collectionItemId: itemId, data: item.data || {} })
            return
        }
        setShowModal(true)
    }

    return (
        <>
            <div
                className="flex items-center gap-3 p-2 bg-gray-50 rounded hover:bg-gray-100 cursor-pointer transition-colors"
                onClick={handleClick}
            >
                {isPeople ? (
                    <PersonAvatar
                        data={item.data || {}}
                        className="w-8 h-8 flex-shrink-0"
                        initialsClassName="text-xs"
                        alt={renderCollectionItem(item, collection)}
                    />
                ) : pictureValue ? (
                    <img
                        src={pictureValue}
                        alt={renderCollectionItem(item, collection)}
                        className="w-10 h-10 object-cover rounded-full flex-shrink-0"
                    />
                ) : null}
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-amber-800 hover:text-amber-900 truncate">
                        {renderCollectionItem(item, collection)}
                    </div>
                </div>
            </div>
            {showModal && (
                <CollectionItemModal
                    collectionId={collectionId}
                    itemId={itemId}
                    onClose={() => setShowModal(false)}
                    onRefresh={onRefresh}
                />
            )}
        </>
    )
}

interface ListValueCardProps {
    feature: Feature
    values: any[]
    collectionItems: Record<string, any>
    onViewCheckins: () => void
    onRefresh?: () => void
    /** Forwarded to `ReferenceListItem` so People rows can open
     *  `PersonDetailModal` instead of the generic collection-item editor. */
    onPersonClick?: (value: PersonDetailValue) => void
}

const ListValueCard: React.FC<ListValueCardProps> = ({ feature, values, collectionItems, onViewCheckins, onRefresh, onPersonClick }) => {
    const listType = feature.config?.params?.type
    const unit = feature.unit || ''

    return (
        <div className="p-4 bg-white rounded-lg shadow-lg border border-gray-200 min-w-[200px] max-w-[400px]">
            <h3 className="font-semibold text-gray-900 mb-3">{feature.title}</h3>

            {/* List items */}
            <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                    {values.length} item{values.length !== 1 ? 's' : ''}
                </p>

                <ul className="space-y-2 max-h-64 overflow-y-auto">
                    {values.map((value, index) => (
                        <li key={index}>
                            {listType === 'reference' && feature.config?.params?.collection_id ? (
                                <ReferenceListItem
                                    collectionId={feature.config.params.collection_id}
                                    itemId={value}
                                    collectionItems={collectionItems}
                                    onRefresh={onRefresh}
                                    onPersonClick={onPersonClick}
                                />
                            ) : (
                                <div className="px-2 py-1 bg-gray-50 rounded text-sm">
                                    {listType === 'num' ? (
                                        <span className="font-mono">
                                            {value}{unit}
                                        </span>
                                    ) : (
                                        <span>{value}</span>
                                    )}
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            </div>
            
            {/* Filter button */}
            <button
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onViewCheckins();
                }}
                className="w-full px-4 py-2 bg-action text-plum rounded hover:bg-action transition-colors text-sm font-medium"
            >
                View checkins containing "{feature.title}"
            </button>
        </div>
    )
}

export default ListValueCard